import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTheme } from './context/ThemeContext';
import { useTypingAnimation } from './hooks/useTypingAnimation';
import { useScrollAnimation } from './hooks/useScrollAnimation';
import { useSpaceBackground } from './hooks/useSpaceBackground';
import { useMouseParticles } from './hooks/useMouseParticles';

import SplashScreen from './components/common/UI/SplashScreen';
import Header from './components/common/Header/Header';
import Hero from './components/sections/Hero/Hero';
import Programs from './components/sections/Programs/Programs';
import Features from './components/sections/Features/Features';
import StaffCarousel from './components/sections/StaffCarousel/StaffCarousel';
import Testimonials from './components/sections/Testimonials/Testimonials';
import Gallery from './components/sections/Gallery/Gallery';
import Footer from './components/common/Footer/Footer';
import ChatModal from './components/common/Modal/ChatModal';
import AuthModal from './components/common/Modal/AuthModal';

// Import profile pages
import TeacherProfile from './profiles/teacher/TeacherProfile';
import StudentProfile from './profiles/student/StudentProfile';
import AdminProfile from './profiles/admin/AdminProfile';

// Main Home Component (your existing app content)
function HomePage() {
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authFormType, setAuthFormType] = useState('login');

  // Initialize hooks
  useSpaceBackground();
  useScrollAnimation();
  useMouseParticles();

  const openAuthModal = (formType = 'login') => {
    setAuthFormType(formType);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => setIsAuthModalOpen(false);
  const toggleChatModal = () => setIsChatModalOpen(!isChatModalOpen);

  return (
    <div className="app">
      <div className="space-background"></div>
      <div className="stars-container" id="starsContainer"></div>

      <Header onOpenAuthModal={openAuthModal} />

      <main>
        <Hero onOpenAuthModal={openAuthModal} />
        <Programs />
        <Features />
        <StaffCarousel />
        <Testimonials />
        <Gallery />
      </main>

      <Footer />

      <button
        className="chat-button"
        onClick={toggleChatModal}
        aria-label="Open chat support"
      >
        <i className="fas fa-comments"></i>
        <span className="tooltip">Need help?</span>
      </button>

      <ChatModal isOpen={isChatModalOpen} onClose={toggleChatModal} />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        formType={authFormType}
      />
    </div>
  );
}

// Layout component to handle different layouts for home vs profile pages
function AppLayout() {
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(true);
  
  // Check if we're on a profile page
  const isProfilePage = location.pathname.includes('-profile');

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (showSplash && !isProfilePage) {
    return <SplashScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/teacher-profile" element={<TeacherProfile />} />
      <Route path="/student-profile" element={<StudentProfile />} />
      <Route path="/admin-profile" element={<AdminProfile />} />
      {/* Add a catch-all route for home */}
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
}

// Main App Component with Router
function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;